// Dev-only fixtures + dispatcher for the two account-level CONNECTIONS the
// console owns end to end: the member's own GitHub token (`/v1/me/github-token`,
// #21, with the repos bound to it, `/v1/me/repo-bindings`) and the BYO log sink
// (`/v1/billing/log-sink`, #448).
//
// One module because they are one shape — a durable connection a human turns on
// and off, read as a status and written by a POST/PUT plus a DELETE — and two
// files for two four-line stores would be split for its own sake.
//
// Split out of `mocks.ts` (plan 101 slice 15 step 4). `null` means "not mine"
// and `mockFetch` falls through, so `billingMock` (seats/spend) and `keysMock`
// (`/v1/billing/keys*`) keep their own halves of those prefixes.
//
// STATEFUL, for the reason the whole family is: `routes/GithubToken` and
// `routes/AccountKeys` both `await query.refetch()` after every write, so a
// stateless echo restored the pre-write status on the next GET — a disconnect
// that reads as a dead button. No `Resource` exists for either body (both are
// masked inline in `apps/api`), so `LogSinkStatus` and `GithubTokenConnection`
// — the dashboard's own types — are what grade them.

import type { GithubTokenConnection } from './github-token';
import type { LogSinkStatus } from './log-sink';
import { at, DAY_MS, inMs, me } from './mock-session';
import type {
  RepoBinding,
  RepoBindingList,
  RepoBindResult,
  RepoUnbindResult,
} from './repo-bindings';

/** The PAT's lifetime on the card: a month out, counted from the request's own clock. */
const TOKEN_TTL_MS = 30 * DAY_MS;

/**
 * The connected state this family holds — the repos the token authorizes, and
 * NOTHING ELSE. The expiry is not stored: it is derived per request (see
 * `connectionOf`), so a stale instant can never outlive the read that named it.
 */
type GithubState = { readonly repos: readonly string[] };

/** A connected sample, so the card opens on its connected state. */
let github: GithubState | null = {
  repos: ['developerz-ai/developerz.ai'],
};

/** The token's repos the member bound to it (`/v1/me/repo-bindings`, arc 101 slice 05). */
let bindings: RepoBinding[] = [
  { repo_id: 'mock-repo-binding-1', full_name: 'developerz-ai/developerz.ai', enabled: true },
];

let sink: LogSinkStatus = {
  configured: true,
  host: 'logs.db.example.com',
  port: 5432,
  database: 'dz_audit',
  updated_at_iso: at('2026-07-01'),
};

/**
 * The wire body for a connected token. The expiry is FRESH per request: the
 * card prints it through `ago()` (`components/GithubTokenCard.tsx`), so it is a
 * DEADLINE, and a deadline is a distance from now — a calendar instant
 * (`at('2027-01-01')`) reads "in 2 months" today and "337 days ago" once the
 * clock is 400 days on, and the card then claims a live credential expired.
 */
function connectionOf(state: GithubState): GithubTokenConnection {
  return { connected: true, expires_at: inMs(TOKEN_TTL_MS), repos: state.repos };
}

function githubToken(method: string, init?: RequestInit): Promise<unknown> | null {
  if (method === 'GET') return Promise.resolve({ connection: github && connectionOf(github) });
  if (method === 'DELETE') {
    github = null;
    // The server drops every binding with the token, in one transaction.
    const unbound = bindings.length;
    bindings = [];
    return Promise.resolve({ connected: false, unbound_repos: unbound });
  }
  if (method !== 'POST') return null;
  const body = init?.body
    ? (JSON.parse(String(init.body)) as { token?: string; repos?: string[] })
    : {};
  github = { repos: body.repos ?? [] };
  // The connect response names the login it resolved; the read does not carry
  // one, which is why this is not simply the stored row.
  return Promise.resolve({ ...connectionOf(github), login: me.user.login });
}

function repoBindings(path: string, method: string, init?: RequestInit): Promise<unknown> | null {
  if (path === '/v1/me/repo-bindings' && method === 'GET')
    return Promise.resolve({ bindings } satisfies RepoBindingList);
  if (path === '/v1/me/repo-bindings' && method === 'POST') {
    const { repo = '' } = init?.body ? (JSON.parse(String(init.body)) as { repo?: string }) : {};
    const binding: RepoBinding = {
      repo_id: `mock-repo-binding-${repo}`,
      full_name: repo,
      enabled: true,
    };
    bindings = [...bindings.filter((b) => b.full_name !== repo), binding];
    return Promise.resolve({
      bound: true,
      repo_id: binding.repo_id,
      full_name: repo,
      created: true,
    } satisfies RepoBindResult);
  }
  const del = /^\/v1\/me\/repo-bindings\/([^/]+)\/([^/]+)$/.exec(path);
  if (del && method === 'DELETE') {
    const repo = `${decodeURIComponent(del[1] ?? '')}/${decodeURIComponent(del[2] ?? '')}`;
    bindings = bindings.filter((b) => b.full_name !== repo);
    return Promise.resolve({ bound: false, full_name: repo } satisfies RepoUnbindResult);
  }
  return null;
}

function logSink(method: string): Promise<unknown> | null {
  if (method === 'GET') return Promise.resolve(sink);
  if (method === 'DELETE') {
    sink = { configured: false };
    return Promise.resolve(sink);
  }
  if (method !== 'PUT') return null;
  // The raw URL is validated and sealed server-side and only the non-secret
  // coordinates come back, so the echo re-states the sample's — this mock does
  // NOT dissect the URL it was handed. A parser here would be a second one
  // beside the server's, and its refusals would be refusals the API never makes.
  sink = {
    configured: true,
    host: 'logs.db.example.com',
    port: 5432,
    database: 'dz_audit',
    updated_at_iso: at('2026-07-24'),
  };
  return Promise.resolve(sink);
}

/** Serve both connections; `null` for anything else. */
export function accountConnectionsMock(
  path: string,
  method: string,
  init?: RequestInit,
): Promise<unknown> | null {
  const bare = path.split('?')[0] ?? path;
  if (bare === '/v1/me/github-token') return githubToken(method, init);
  if (bare.startsWith('/v1/me/repo-bindings')) return repoBindings(bare, method, init);
  if (bare === '/v1/billing/log-sink') return logSink(method);
  return null;
}

/** Restore the pristine fixtures — the shared reset seam (`./mock-state.ts`). */
const PRISTINE_GITHUB = structuredClone(github);
const PRISTINE_SINK = structuredClone(sink);
const PRISTINE_BINDINGS = structuredClone(bindings);

export function resetAccountConnectionsMocks(): void {
  github = structuredClone(PRISTINE_GITHUB);
  sink = structuredClone(PRISTINE_SINK);
  bindings = structuredClone(PRISTINE_BINDINGS);
}
